// import React from 'react';
import Typography from '@mui/material/Typography';
import { GovernanceComplianceClobDto, DiversityBreakdown } from 'models/dtoTypes';
import { sortBy } from 'lodash';
import List from '@mui/material/List';
import SubSection from './_subSection';
import Piechart from 'components/Piechart';
import { governanceSvg } from 'kosmos/images';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const PREFIX = 'GovernanceReport';

const classes = {
  content: `${PREFIX}-content`,
  icon: `${PREFIX}-icon`,
  panel: `${PREFIX}-panel`,
  item: `${PREFIX}-item`,
  pieContainer: `${PREFIX}-pieContainer`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(4),
  width: '100%',
  [`& .${classes.icon} > img`]: {
    width: 70,
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    marginLeft: -15,
    marginRight: -15,
  },
  [`& .${classes.panel}`]: {
    flex: 1,
    padding: '5px 15px',
  },
  [`& .${classes.item}`]: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  [`& .${classes.pieContainer}`]: {
    height: 350,
    maxWidth: 600,
  },
}));

interface IProps {
  negativeGovernance: GovernanceComplianceClobDto[];
  positiveGovernance: GovernanceComplianceClobDto[];
}

export default function GovernanceReport(props: IProps): JSX.Element {
  const { negativeGovernance, positiveGovernance } = props;
  const { t } = useTranslation('default');
  const renderTitle = (title: string) => {
    return (
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'regular' }}>
        {title}
      </Typography>
    );
  };

  const generatePieData = (breakdown: DiversityBreakdown) => {
    const {
      percentMale,
      percentFemale,
      percentUnknown,
      absoluteFemale,
      absoluteMale,
      absoluteUnknown,
      totalDirectors = 0,
    } = breakdown;
    return [
      {
        id: 'male',
        label: `${percentMale || 0}% ${t('male')}`,
        tooltip: `${percentMale || 0}% ${t('male')} (${absoluteMale || 0} of ${totalDirectors || 0} ${t('board-directors')})`,
        value: percentMale || 0,
        color: 'hsl(69, 70%, 50%)',
      },
      {
        id: 'female',
        label: `${percentFemale || 0}% ${t('female')}`,
        tooltip: `${percentFemale || 0}% ${t('female')} (${absoluteFemale || 0} of ${totalDirectors || 0} ${t('board-directors')})`,
        value: percentFemale || 0,
        color: 'hsl(324, 70%, 50%)',
      },
      {
        id: 'did-not-say',
        label: `${percentUnknown || 0}% ${t('did-not-say')}`,
        tooltip: `${percentUnknown || 0}% ${t('unknown')} (${absoluteUnknown || 0} of ${totalDirectors || 0} ${t('board-directors')})`,
        value: percentUnknown || 0,
        color: 'hsl(21, 70%, 50%)',
      },
    ];
  };

  const renderString = (label: string, isNested?: boolean) => {
    return (
      <Typography
        key={label}
        className={classes.item}
        sx={{
          pl: isNested ? 3 : 0,
          mb: 1,
        }}
      >
        {label}
      </Typography>
    );
  };

  const renderPieChart = (breakdown: DiversityBreakdown) => {
    const data = generatePieData(breakdown);
    return (
      <div className={classes.pieContainer}>
        <Piechart data={data} />
      </div>
    );
  };

  const renderSingleItem = (item: GovernanceComplianceClobDto, index: number) => {
    const title = item.title || '';
    const breakdown = item.breakdown;
    return (
      <div key={index}>
        {renderString(title)}
        {breakdown && renderPieChart(breakdown)}
      </div>
    );
  };

  const renderList = (list: GovernanceComplianceClobDto[]) => {
    const sortedList = sortBy(list, (item) => item.itemOrder);
    return <List>{sortedList.map(renderSingleItem)}</List>;
  };

  return (
    <SubSection isBold title={t('governance-compliance')} icon={<img src={governanceSvg} />}>
      <Root>
        <div className={classes.content}>
          <div className={classes.panel}>
            {renderTitle('*' + t('data-aggregated-through-open-source-information'))}
            {renderList(positiveGovernance)}
          </div>
          <div className={classes.panel}>
            {renderTitle(t('the-company-does-not-comply-with-the-following-governance-conditions'))}
            {renderList(negativeGovernance)}
          </div>
        </div>
      </Root>
    </SubSection>
  );
}
